import { memo, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import api from '@/lib/api';

/**
 * Meeting-note create form, extracted from ProjectDetailPanel so that
 * keystrokes only re-render this small subtree instead of the entire
 * 1600-line panel. Fixes the "typing lag / letters appear in bursts"
 * bug caused by ReactQuill controlled-value reconciliation being paced
 * against the panel's expensive full re-render.
 */
function MeetingNoteCreateForm({ projectId, onSaved, onCancel, onLogTime }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    attendees: '',
    notes: '',
  });

  const setDate = useCallback((e) => setForm(f => ({ ...f, date: e.target.value })), []);
  const setAttendees = useCallback((e) => setForm(f => ({ ...f, attendees: e.target.value })), []);
  const setNotes = useCallback((v) => setForm(f => ({ ...f, notes: v })), []);

  const save = async (alsoLogTime = false) => {
    if (!form.notes.trim()) return toast.error('Notes required');
    await api.post(`/projects/${projectId}/meeting-notes`, form);
    toast.success('Meeting note added');
    const carriedNotes = (() => {
      const tmp = document.createElement('div');
      tmp.innerHTML = form.notes;
      return (tmp.textContent || tmp.innerText || '').trim();
    })();
    onSaved?.();
    if (alsoLogTime) onLogTime?.(carriedNotes);
  };

  return (
    <Card className="border-border border-dashed bg-transparent shadow-none">
      <CardContent className="p-3 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">New Meeting Note</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={setDate}
              className="bg-background border-border"
              data-testid="meeting-note-date"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Attendees</Label>
            <Input
              placeholder="e.g. John, Sarah, Mike"
              value={form.attendees}
              onChange={setAttendees}
              className="bg-background border-border"
              data-testid="meeting-note-attendees"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes *</Label>
          <RichTextEditor
            placeholder="Meeting notes..."
            value={form.notes}
            onChange={setNotes}
            data-testid="meeting-note-content"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => save(false)}
            className="bg-primary text-primary-foreground"
            data-testid="save-meeting-note-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Save Note
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => save(true)}
            className="border-primary/40 text-primary hover:bg-primary/10"
            data-testid="save-meeting-note-log-time-btn"
          >
            <Clock className="h-3 w-3 mr-1" /> Save Note + Log Time
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(MeetingNoteCreateForm);
